import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import CustomizablePopover from './CustomizablePopover';

/**
 * Custom hook to manage popover state and behavior with hover support
 * Returns both raw handlers and a pre-configured Popover component for convenience
 * @param {number} closeDelay - Delay in milliseconds before closing popover (default: 150)
 * @returns {Object} Popover state, handlers, and component renderer
 */
export const useCustomizablePopover = (closeDelay = 150) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const isHoveringPopoverRef = useRef(false);
  const isHoveringAnchorRef = useRef(false);
  const closeTimerRef = useRef(null);
  const popoverRef = useRef();

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const handlePopoverOpen = useCallback(event => {
    event.stopPropagation();
    // Clear any pending close timer
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    isHoveringAnchorRef.current = true;
    setAnchorEl(event.currentTarget);
  }, []);

  const handlePopoverClose = useCallback(
    event => {
      if (event) {
        event.stopPropagation();
      }
      isHoveringAnchorRef.current = false;
      // Delay closing to allow moving to popover
      closeTimerRef.current = setTimeout(() => {
        if (!isHoveringPopoverRef.current && !isHoveringAnchorRef.current) {
          setAnchorEl(null);
        }
      }, closeDelay);
    },
    [closeDelay]
  );

  const handlePopoverMouseEnter = () => {
    // Clear any pending close timer when entering popover
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    isHoveringPopoverRef.current = true;
  };

  const handlePopoverMouseLeave = () => {
    isHoveringPopoverRef.current = false;
    // Use the same delay when leaving popover to prevent flickering
    closeTimerRef.current = setTimeout(() => {
      if (!isHoveringPopoverRef.current && !isHoveringAnchorRef.current) {
        setAnchorEl(null);
      }
    }, closeDelay);
  };

  const forceClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    isHoveringPopoverRef.current = false;
    isHoveringAnchorRef.current = false;
    setAnchorEl(null);
  };

  const openPopover = Boolean(anchorEl);

  /**
   * Renders a pre-configured CustomizablePopover with the hook's state
   * @param {Object} props - CustomizablePopover props (id, content, etc.)
   */
  const Popover = ({ id, popoverHeaderContent, popoverMainContent, popoverFooterContent }) => (
    <CustomizablePopover
      anchorEl={anchorEl}
      handlePopoverClose={handlePopoverClose}
      id={id}
      onMouseEnter={handlePopoverMouseEnter}
      onMouseLeave={handlePopoverMouseLeave}
      openPopover={openPopover}
      popoverFooterContent={popoverFooterContent}
      popoverHeaderContent={popoverHeaderContent}
      popoverMainContent={popoverMainContent}
    />
  );

  Popover.propTypes = {
    id: PropTypes.string.isRequired,
    popoverFooterContent: PropTypes.node,
    popoverHeaderContent: PropTypes.node,
    popoverMainContent: PropTypes.node,
  };

  Popover.defaultProps = {
    popoverFooterContent: null,
    popoverHeaderContent: null,
    popoverMainContent: null,
  };

  return {
    // Pre-configured component (listed first for convenience)
    Popover,
    // Raw state and handlers (for advanced use cases)
    anchorEl,
    closeTimerRef,
    forceClose,
    handlePopoverClose,
    handlePopoverMouseEnter,
    handlePopoverMouseLeave,
    handlePopoverOpen,
    openPopover,
    popoverRef,
  };
};

export default useCustomizablePopover;
